import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { exchangeGoogleToken } from '../Utilities/api';
import { loadGoogleScript, GOOGLE_CLIENT_ID } from '../Utilities/googleAuth';
import { useAuth } from '../Utilities/authContext';
import './login.css';

function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const buttonRef = useRef(null);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [oauthError, setOauthError] = useState(null);

  // Facebook OAuth failures land back at `/?fb_error=<reason>` per BE contract.
  useEffect(() => {
    const reason = searchParams.get('fb_error');
    if (!reason) return;
    setOauthError(`Facebook linking failed: ${reason.replace(/_/g, ' ')}.`);
    const next = new URLSearchParams(searchParams);
    next.delete('fb_error');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      setError(
        'Google OAuth is not configured. Set REACT_APP_GOOGLE_CLIENT_ID at build time.'
      );
      return;
    }

    let cancelled = false;

    const handleCredentialResponse = async (response) => {
      try {
        const { jwt, user } = await exchangeGoogleToken(response.credential);
        localStorage.setItem('jwt', jwt);
        setUser(user);
        navigate('/dashboard');
      } catch (err) {
        console.error(err);
        setError('Sign-in failed. Please try again.');
      }
    };

    loadGoogleScript()
      .then((google) => {
        if (cancelled) return;
        google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        if (buttonRef.current) {
          google.accounts.id.renderButton(buttonRef.current, {
            theme: 'outline',
            size: 'large',
            type: 'standard',
            shape: 'rectangular',
            text: 'continue_with',
            logo_alignment: 'left',
            width: 320,
          });
        }
      })
      .catch((err) => {
        console.error(err);
        setError('Could not load Google sign-in. Check your network and try again.');
      });

    return () => {
      cancelled = true;
    };
  }, [navigate, setUser]);

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand">Single Short</div>
        <h1 className="login-title">Your content. Every platform. One view.</h1>
        <p className="login-subtitle">
          Group your accounts, link Instagram, TikTok, and YouTube, and pull every upload
          and stat into a single queryable feed — on demand.
        </p>

        <div className="login-google-slot" ref={buttonRef} />

        {oauthError && <div className="login-error">{oauthError}</div>}
        {error && <div className="login-error">{error}</div>}

        <div className="login-legal">
          By continuing you agree to our{' '}
          <a href="/terms-of-service">Terms of Service</a> and{' '}
          <a href="/privacy-policy">Privacy Policy</a>.
        </div>
      </div>

      <div className="login-footer">Single Short — unified content & analytics</div>
    </div>
  );
}

export default Login;
